import * as api from 'app/shared/api';
import { ThunkDispatch } from 'redux-thunk';
import { QueryParamsMap } from 'app/RouteDefinition';
import { Action, ViewPageLoadedData, EditPageLoadedData } from 'app/pages/AgentPricing/actionTypes';
import { viewPageLoaded, editPageLoaded } from 'app/pages/AgentPricing/actions';
import {AgentCommissionDto, AgentCommissionStatus} from 'app/shared/model/AgentCommissionDto';
import { Agent } from 'app/shared/model/Agent';
import {getSelectedDivisionId} from "app/pages/AgentPricing/utils/division";

function resolveAgent(agents: Agent[], agentId: string, fallbackName?: string): Agent {
    const agent = agents.find(x => x.id === agentId);
    if (agent) return agent;

    return {
        id: agentId,
        name: fallbackName || agentId,
        offices: [],
        status: '',
        ownerFullName: '',
        ownerEmail: '',
        countryName: '',
        countryIsoCode: '',
        classification: '',
    };
}

type ViewAgentCommissionAndData = {
    agentCommission: AgentCommissionDto,
    data: ViewPageLoadedData
};

const EMPTY_AGENT_COMMISSION: AgentCommissionDto = {
    startDate: '2000-01-01',
    endDate: undefined,
    minChangeDate: '2000-01-01',
    status: AgentCommissionStatus.New,
    countries: [],
    versionCode: '',
    rules: []
};
type AgentCommissionLoadResult = {
    commission: AgentCommissionDto;
    hasNoCommission: boolean;
};

function isNoCommissionForDivisionError(error: unknown): boolean {
    const message = String((error as any)?.message ?? error ?? '');
    return message.includes('HTTP 400')
        || message.includes('Object reference not set to an instance of an object.');
}

function getLatestAgentCommissionOrEmpty(scopeId: string, divisionId: number): Promise<AgentCommissionLoadResult> {
    return api.getLatestAgentCommission(scopeId, divisionId)
        .then(commission => ({
            commission,
            hasNoCommission: false
        }))
        .catch(error => {
            if (isNoCommissionForDivisionError(error)) {
                return {
                    commission: EMPTY_AGENT_COMMISSION,
                    hasNoCommission: true
                };
            }

            throw error;
        });
}

export function loadViewPage(dispatch: ThunkDispatch<any, any, Action>, qs: QueryParamsMap) : Promise<void> {
    const id = qs.id;
    if (id == undefined)
        throw new Error('Missing agent id');

    const divisionId = getSelectedDivisionId();
    if (!divisionId) throw new Error('Missing divisionId (selectedDivisionId in localStorage)');

    return fetchViewPageData(id, divisionId, qs.name as string | undefined)
        .then(x => {
            dispatch(viewPageLoaded(x.agentCommission, x.data));
    });
}

function fetchViewPageData(agentId: string, divisionId: number, fallbackName?: string): Promise<ViewAgentCommissionAndData> {
    const agentCommission = getLatestAgentCommissionOrEmpty(agentId, divisionId);
    const agent = api.getAgentsById(false, agentId);
    const countries = api.getAllCountries();
    const schools = api.getAllSchools();
    const products = api.getAllProducts();
    const fees = api.getAllFees();
    
    return Promise.all([agentCommission, agent, countries, schools, products, fees])
        .then(serverData => {
            const commissionResult = serverData[0];
            const agentsFromApi = serverData[1];
            const hasExternalAgentData = agentsFromApi.length > 0;

            const agent = resolveAgent(agentsFromApi, agentId, fallbackName);

            const data : ViewPageLoadedData = {
               id: agentId,
               name: agent.name,
               countries: serverData[2],
               schools: serverData[3],
               products: serverData[4],
               fees: serverData[5],
               offices: agent.offices ?? [],
                hasNoCommissionGroup: commissionResult.hasNoCommission,
                hasExternalAgentData,
                status: agent.status
            };

           return {
                agentCommission: commissionResult.commission,
                data
            };
        });
}

export function loadEditDiscountPage(dispatch: ThunkDispatch<any, any, Action>, qs: QueryParamsMap) : Promise<void> {
    const id = qs.id;
    if (id == undefined)
        throw new Error('Missing agent id');

    const divisionId = (qs.divisionId ? Number(qs.divisionId) : undefined) ?? getSelectedDivisionId();
    if (!divisionId) throw new Error('Missing divisionId (selectedDivisionId in localStorage)');

    return fetchEditPageData(id, divisionId, qs.groupId as string | undefined)
        .then(x => {
            dispatch(editPageLoaded(x.agentCommission, x.data));
    });
}

interface EditPageDataResult {
    agentCommission: AgentCommissionDto,
    data: EditPageLoadedData
}

function fetchEditPageData(scopeId: string, divisionId: number, groupId?: string): Promise<EditPageDataResult> {
    const agentLookupId = groupId ?? scopeId;
    const agentCommission = getLatestAgentCommissionOrEmpty(scopeId, divisionId);
    const agent = api.getAgentsById(false, agentLookupId);
    const countries = api.getAllCountries();
    const schools = api.getAllSchools();
    const centres = api.getAllCentres();

    return Promise.all([agentCommission, agent, countries, schools, centres])
        .then(serverData => {
            const commissionResult = serverData[0];
            const agentsFromApi = serverData[1];
            const hasExternalAgentData = agentsFromApi.length > 0;

            const agentOrGroup = resolveAgent(agentsFromApi, agentLookupId);

            const displayName = groupId
                ? (agentOrGroup.offices ?? []).find((o: { id: string }) => o.id === scopeId)?.name ?? agentOrGroup.name
                : agentOrGroup.name;

            const data : EditPageLoadedData = {
               id: scopeId,
               name: displayName,
               countries: serverData[2],
               schools: serverData[3],
               centres: serverData[4],
               groupId: groupId,
                hasNoCommissionGroup: commissionResult.hasNoCommission,
                hasExternalAgentData,
                status: agentOrGroup.status
            };

           return {
                agentCommission: commissionResult.commission,
                data
            };
        });
}